/**
 * Inventory Policy resolution, Meteor-free so it is unit testable.
 *
 * A policy never decides WHICH cards a bot stocks -- membership always comes
 * from the effective sell selection (bot.config.sellQuery -> fleet ->
 * Settings -> legacy sellTargets). A policy decides HOW MANY copies: different
 * fleets/bots targeting the same cards can hold different stock depths.
 *
 * Rules are evaluated in listed order, first match wins. Within one rule,
 * every predicate present must match (AND) -- so { sets: ['MSH'], foil: true,
 * qty: 2 } is "MSH foils stock 2". A rule with no predicates matches every
 * card (making later rules dead); `defaultQty` answers when no rule matches.
 * No match and no defaultQty -> null, and the caller falls through to the
 * sellCap/stocksize chain, i.e. exactly pre-policy behaviour.
 *
 * qty 0 means "don't restock, and deplete held copies": replenish orders 0
 * and the deplete cycle treats every held copy as excess. The TRADEABLE cap
 * deliberately ignores qty 0 (falls through to sellCap/stocksize) so held
 * copies can still be sold to customers while awaiting depletion.
 *
 * `format` can only mean the pauper/legacy card flags -- Standard/Pioneer/
 * Modern legality does not exist in the MTGO card data.
 */

export interface PolicyRule {
  /** Explicit card ids (Card._id). */
  cardIds?: number[];
  /** Set CODES (Card.setName), e.g. ['MSH', 'HOB']. Case-insensitive. */
  sets?: string[];
  /** Card format flag: the card's `pauper` or `legacy` boolean must be true. */
  format?: 'pauper' | 'legacy';
  /** Match foil (true) or non-foil (false) printings. */
  foil?: boolean;
  /** TARGET quantity: copies to carry. Drives replenishment and the deplete
   *  target. 0 = don't restock + deplete held. */
  qty: number;
  /** ADVERTISED quantity: max copies offered for sale per trade (the binder
   *  cap). Must not exceed qty. 0 = carry but do not offer. Unset falls
   *  through to the sellCap/shelfsize chain (default 4). */
  adQty?: number;
}

export interface PolicyLike {
  defaultQty?: number | null;
  /** Advertised-quantity fallback when the matched rule has no adQty (or no
   *  rule matches). Same constraint: never exceeds the resolved target. */
  defaultAdQty?: number | null;
  rules?: PolicyRule[];
}

/** The card fields a rule can see -- satisfied by both the Card doc and the
 *  bot's Card class instance. */
export interface PolicyCard {
  _id: number;
  setName?: string;
  pauper?: boolean;
  legacy?: boolean;
  isfoil?: boolean;
}

const validQty = (q: unknown): q is number =>
  typeof q === 'number' && Number.isFinite(q) && q >= 0;

export const ruleMatches = (rule: PolicyRule, card: PolicyCard): boolean => {
  if (rule.cardIds !== undefined && !rule.cardIds.includes(card._id)) return false;
  if (rule.sets !== undefined) {
    const set = (card.setName ?? '').toUpperCase();
    if (!rule.sets.some((s) => String(s).toUpperCase() === set)) return false;
  }
  if (rule.format !== undefined && !card[rule.format]) return false;
  if (rule.foil !== undefined && !!card.isfoil !== rule.foil) return false;
  return true;
};

/** The first matching rule with a valid qty -- ONE rule decides both of a
 *  card's quantities, so target and advertised can never come from different
 *  rules. */
const matchedRule = (policy: PolicyLike, card: PolicyCard): PolicyRule | null => {
  for (const rule of policy.rules ?? []) {
    if (validQty(rule?.qty) && ruleMatches(rule, card)) return rule;
  }
  return null;
};

/** The policy's stock TARGET for one card, or null to fall through to the
 *  sellCap/stocksize chain. 0 is a real answer -- callers must not `||` it. */
export const policyQtyFor = (policy: PolicyLike, card: PolicyCard): number | null => {
  const rule = matchedRule(policy, card);
  if (rule) return Math.floor(rule.qty);
  return validQty(policy.defaultQty) ? Math.floor(policy.defaultQty) : null;
};

/** The policy's ADVERTISED quantity (binder cap) for one card, or null to
 *  fall through to the sellCap/shelfsize chain. Clamped to the resolved
 *  target -- advertised can never exceed what the bot aims to carry. */
export const policyAdQtyFor = (policy: PolicyLike, card: PolicyCard): number | null => {
  const rule = matchedRule(policy, card);
  const raw = validQty(rule?.adQty) ? rule!.adQty!
    : validQty(policy.defaultAdQty) ? policy.defaultAdQty
      : null;
  if (raw === null) return null;
  const target = policyQtyFor(policy, card);
  return Math.floor(target !== null ? Math.min(raw, target) : raw);
};

/** Mongo query against `cards` matching this rule alone -- used for the
 *  per-rule hit counts on the policy page. Predicate keys are disjoint, so a
 *  plain merge is a correct AND. */
export const ruleToQuery = (rule: PolicyRule): Record<string, unknown> => {
  const q: Record<string, unknown> = {};
  if (rule.cardIds !== undefined) q._id = { $in: rule.cardIds };
  if (rule.sets !== undefined) q.setName = { $in: rule.sets.map((s) => String(s).toUpperCase()) };
  if (rule.format !== undefined) q[rule.format] = true;
  if (rule.foil !== undefined) q.isfoil = rule.foil;
  return q;
};
